import classNames from 'classnames';
import { TablePanel as OriginalTablePanel } from 'eone-ui/panels/table-panel';
import { TableFieldType } from 'eone-ui/tables/table-constants';
import { useTableState } from 'eone-ui/tables/useTableState';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';
import { useMeasure } from 'react-use';
import { TableRowCheckbox } from './table_row_checkbox';
import { TableRowSelect } from './table_row_select';
import { EnumTableFieldType, type BasicRecord, type SCAdvanceTableProps } from './types';
import { useMenuButtonPortal } from './use_menu_button_portal';
import { useRowButtons } from './use_row_buttons';
import { useSelect } from './use_select';
import { useAdvanceTableStateCache } from './use_table_state';
import { useTree } from './use_tree';

const SCAdvanceTableWithCache = <D extends BasicRecord>(props: SCAdvanceTableProps<D>) => {
  const tableState = useAdvanceTableStateCache(props.Id!, props.InitialTableState);
  return (
    <div
      className={classNames({
        'sc-advance-table-fixed-row-buttons': props.FixedRowButtons,
      })}
    >
      <OriginalTablePanel {...props} TableState={tableState} />
    </div>
  );
};

const SCAdvanceTable = <D extends BasicRecord>(props: SCAdvanceTableProps<D>) => {
  const tableState = useTableState(
    props.InitialTableState?.InitialSortField,
    props.InitialTableState?.InitialSortDirection,
    props.InitialTableState?.InitialColumns,
    props.InitialTableState?.InitialSearch,
    props.InitialTableState?.InitialFilterValues,
  );

  const fixProps = useMemo(() => {
    // Important Note: doing only shadow clone here, mind the logic below is mutating the original objects passed in like props.Columns
    const nProps = { ...props };
    const useSuccessForLoading = !isNil(props.Success) && !props.Success; // e.g. success=false
    if (useSuccessForLoading) {
      nProps.Loading = props.Loading || useSuccessForLoading;
    }
    if (props.ReadOnly) {
      nProps.Reorderable = false;
      nProps.Columns?.map((col) => {
        // Note: use `col.ReadOnly` instead of `.Editable`
        // to support dropdown red-frames feature with View mode
        // col.Editable = false;
        col.ReadOnly = true;
      });
    }
    // fix props. when header buttons are present but title is not set,
    // set title to a zero-width space so the PanelHeader still renders.
    // Using \u200B (zero-width space, Unicode Cf) because regular space and \u00A0
    // are both stripped by String.trim() in eone-ui's PanelHeader guard.
    // Note: Search alone does not force the header to render — the title container
    // should be hidden if there is no title or header buttons.
    if (props.HeaderButtons && props.HeaderButtons.length > 0 && !props.Title) {
      nProps.Title = '\u200B';
    }

    if (props.Columns.length) {
      // disappear selected options
      const selectColumns = nProps.Columns.filter(
        (col) => col.FieldType === EnumTableFieldType.Select && col.Editable,
      );
      if (selectColumns.length) {
        selectColumns.forEach((column) => {
          column.RenderRow = ({ ColumnIndex, Row, Column }) => {
            const visible = Column.CellVisible && Row[Column.CellVisible];
            const fieldType = Column.FieldType || Row[Column.RowFieldType] || TableFieldType.String;
            const options = Column.Options || Row[Column.RowOptions] || [];
            const valueTypes = Column.ValueTypes || Row[Column.RowValueTypes] || [];
            const readOnly = Column.CellReadOnly
              ? Row[Column.CellReadOnly] || column.ReadOnly
              : column.ReadOnly;
            return (
              <TableRowSelect
                key={ColumnIndex}
                FieldType={fieldType}
                ValueTypes={valueTypes}
                Options={options}
                Row={Row}
                Visible={visible}
                ReadOnly={readOnly}
                // bugfix: making table-row-select works like table-row-checkbox
                // ChangeValue={(IdField, Value) => {
                ChangeValue={(Field, Value) => {
                  // props.ChangeValue?.(IdField, `${RowIndex}`, Value);
                  props.ChangeValue?.(Row[props.IdField], Field, Value);
                }}
                {...Column}
              />
            );
          };
        });
      }

      // supporting Disabled-field for TableCheckboxField
      const checkboxAdvancedColumns = nProps.Columns.filter(
        (col) => col.FieldType === EnumTableFieldType.CheckboxAdvanced,
      );
      checkboxAdvancedColumns.forEach((column) => {
        column.RenderRow = ({ ColumnIndex, Row, Column }) => {
          const disabled = Column.CellDisabled && Row[Column.CellDisabled];
          return (
            <TableRowCheckbox
              key={ColumnIndex}
              Row={Row}
              Disabled={disabled || Column.ReadOnly || !Column.Editable}
              ChangeValue={(Field, Value) => {
                props.ChangeValue?.(Row[props.IdField], Field, Value);
              }}
              {...Column}
            />
          );
        };
      });
    }
    return nProps;
  }, [props]);

  const withSelectProps = useSelect(fixProps) as SCAdvanceTableProps<D>;
  const withTreeProps = useTree(withSelectProps) as SCAdvanceTableProps<D>;

  // Workaround: eone-ui's TableRowButton ignores MenuItems so dropdowns never render.
  // Strip MenuItems out of RowButtons and manage the dropdown via a React portal instead,
  // reusing eone-ui's own CSS classes so the appearance is identical.
  const { strippedButtons, portalElement } = useMenuButtonPortal(
    withTreeProps.RowButtons,
    props.DataTestId,
  );
  const withProps = { ...withTreeProps, RowButtons: strippedButtons };

  const newProps = useRowButtons(withProps);
  // Merge any caller-supplied TableState overlay onto the internally-derived
  // tableState so callers can layer in ViewMode/DetailsPanelOpen/etc. without
  // losing search/sort/columns. Backward compatible: when no overlay is passed,
  // behavior is unchanged.
  const effectiveTableState = props.TableState
    ? { ...tableState, ...props.TableState }
    : tableState;

  if (props.Id && !props.TableState) {
    return (
      <>
        <SCAdvanceTableWithCache
          MaxHeightWithBreadcrumb={props.MaxHeightWithBreadcrumb}
          {...newProps}
        />
        {portalElement}
      </>
    );
  }

  return (
    <div
      style={{ height: '100%' }}
      className={classNames({ 'sc-advance-table-fixed-row-buttons': props.FixedRowButtons })}
    >
      <OriginalTablePanel
        {...newProps}
        MaxHeightWithBreadcrumb={props.MaxHeightWithBreadcrumb}
        TableState={effectiveTableState}
      />
      {portalElement}
    </div>
  );
};

const SCAdvanceTableWithAutoSize = <D extends object>(props: SCAdvanceTableProps<D>) => {
  const [ref, size] = useMeasure();

  return (
    <div className="sce-advance-table-auto-size" ref={ref as any}>
      {size && <SCAdvanceTable {...props} />}
    </div>
  );
};

export { SCAdvanceTable, SCAdvanceTableWithAutoSize };
